// Real OCR Service using Tesseract.js
import Tesseract from 'tesseract.js';

// Extract text from image using Tesseract OCR
export const extractTextFromImage = async (imageFile, onProgress) => {
  try {
    const result = await Tesseract.recognize(
      imageFile,
      'eng', // English language
      {
        logger: (m) => {
          if (onProgress && m.status === 'recognizing text') {
            onProgress(Math.round(m.progress * 100));
          }
        }
      }
    );

    return {
      success: true,
      text: result.data.text,
      confidence: result.data.confidence
    };
  } catch (error) {
    console.error('OCR Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Parse Pakistani bank receipt text to extract transaction details
export const parseReceiptText = (text) => {
  const result = {
    senderName: null,
    senderAccount: null,
    receiverName: null,
    receiverAccount: null,
    amount: null,
    transactionId: null,
    referenceNumber: null,
    date: null,
    bankName: null,
    rawText: text
  };

  // Normalize text - uppercase and clean
  const normalizedText = text.toUpperCase();
  const lines = normalizedText.split('\n').map(l => l.trim()).filter(l => l);

  // Extract Amount (PKR, RS, RS., AMOUNT patterns)
  const amountPatterns = [
    /(?:PKR|RS\.?|AMOUNT|TOTAL)[\s:]*([0-9,]+(?:\.[0-9]{2})?)/gi,
    /([0-9]{1,3}(?:,?[0-9]{3})+(?:\.[0-9]{2})?)\s*(?:PKR|RS|RUPEES)/gi,
    /AMOUNT[\s:]*([0-9,]+)/gi,
    /PAID[\s:]*([0-9,]+)/gi,
  ];

  for (const pattern of amountPatterns) {
    const match = normalizedText.match(pattern);
    if (match) {
      const numMatch = match[0].match(/([0-9,]+(?:\.[0-9]{2})?)/);
      if (numMatch) {
        result.amount = parseFloat(numMatch[1].replace(/,/g, ''));
        break;
      }
    }
  }

  // Extract Transaction ID / Reference Number
  const txnPatterns = [
    /(?:TRANSACTION\s*(?:ID|NO|NUMBER|REF)|TXN\s*(?:ID|NO)|TRANS\s*ID|REFERENCE\s*(?:NO|NUMBER|ID)|REF\s*(?:NO|NUMBER|ID))[\s:]*([A-Z0-9-]+)/gi,
    /(?:STAN|RRN|TRACE)[\s:]*([0-9]+)/gi,
    /REF[-\s]*(\d{4,}[-]?[A-Z0-9]*)/gi,
  ];

  for (const pattern of txnPatterns) {
    const match = normalizedText.match(pattern);
    if (match) {
      const idMatch = match[0].match(/([A-Z0-9-]{6,})/i);
      if (idMatch) {
        result.transactionId = idMatch[1];
        break;
      }
    }
  }

  // Extract Bank Transaction ID (the bank's own transaction/reference number from receipt)
  // This is different from our ANB reference - this is what the bank generates
  const bankTxnPatterns = [
    /(?:TID|TRANSACTION\s*ID|TRANS\s*ID|TXN\s*ID)[\s:]*([A-Z0-9]{6,20})/gi,
    /(?:REFERENCE\s*(?:NO|NUMBER|ID)|REF\s*(?:NO|NUMBER|ID))[\s:]*([A-Z0-9]{6,20})/gi,
    /(?:STAN|RRN|UTR|TRACE\s*NO)[\s:]*([0-9]{6,20})/gi,
    /(?:RECEIPT\s*NO|VOUCHER\s*NO)[\s:]*([A-Z0-9]{6,20})/gi,
  ];

  for (const pattern of bankTxnPatterns) {
    const matches = [...normalizedText.matchAll(pattern)];
    if (matches.length > 0) {
      // Get the captured group (the actual ID)
      result.transactionId = matches[0][1];
      break;
    }
  }

  // Extract Reference Number (our REF-YYYYMM-XXXXX format if present in remarks)
  const refPattern = /REF[-\s]?(\d{6}[-\s]?[A-Z0-9]{6})/gi;
  const refMatch = normalizedText.match(refPattern);
  if (refMatch) {
    result.referenceNumber = refMatch[0].replace(/\s/g, '');
  }

  // Extract Account Numbers (11-16 digit numbers)
  const accountPattern = /(?:A\/C|ACCOUNT|ACC)[\s:NO.]*(\d{10,16})/gi;
  const accountMatches = normalizedText.matchAll(accountPattern);
  const accounts = [...accountMatches].map(m => m[1]);

  if (accounts.length >= 1) {
    result.senderAccount = accounts[0];
  }
  if (accounts.length >= 2) {
    result.receiverAccount = accounts[1];
  }

  // Extract Names (FROM, TO, SENDER, BENEFICIARY patterns)
  const fromPatterns = [
    /(?:FROM|SENDER|REMITTER|ACCOUNT\s*TITLE)[\s:]*([A-Z\s]{3,40})/gi,
  ];

  for (const pattern of fromPatterns) {
    const match = normalizedText.match(pattern);
    if (match) {
      const nameMatch = match[0].match(/[\s:]([A-Z\s]{3,40})/);
      if (nameMatch) {
        result.senderName = nameMatch[1].trim();
        break;
      }
    }
  }

  const toPatterns = [
    /(?:TO|BENEFICIARY|RECEIVER|RECIPIENT)[\s:]*([A-Z\s]{3,40})/gi,
  ];

  for (const pattern of toPatterns) {
    const match = normalizedText.match(pattern);
    if (match) {
      const nameMatch = match[0].match(/[\s:]([A-Z\s]{3,40})/);
      if (nameMatch) {
        result.receiverName = nameMatch[1].trim();
        break;
      }
    }
  }

  // Extract Date
  const datePatterns = [
    /(\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4})/g,
    /(\d{1,2}\s+(?:JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)[A-Z]*\s+\d{2,4})/gi,
  ];

  for (const pattern of datePatterns) {
    const match = normalizedText.match(pattern);
    if (match) {
      result.date = match[0];
      break;
    }
  }

  // Detect Bank Name
  const banks = ['HBL', 'MCB', 'UBL', 'ABL', 'MEEZAN', 'JAZZCASH', 'EASYPAISA', 'BANK ALFALAH', 'STANDARD CHARTERED', 'FAYSAL', 'ASKARI', 'ALLIED BANK', 'HABIB BANK', 'MUSLIM COMMERCIAL', 'UNITED BANK'];

  for (const bank of banks) {
    if (normalizedText.includes(bank)) {
      result.bankName = bank;
      break;
    }
  }

  return result;
};

// Main function to process receipt image
export const processReceiptImage = async (imageFile, onProgress) => {
  // Step 1: Extract text using OCR
  const ocrResult = await extractTextFromImage(imageFile, onProgress);

  if (!ocrResult.success) {
    return {
      success: false,
      error: ocrResult.error
    };
  }

  // Step 2: Parse the extracted text
  const parsedData = parseReceiptText(ocrResult.text);

  return {
    success: true,
    data: parsedData,
    confidence: ocrResult.confidence,
    processedAt: new Date().toISOString()
  };
};

/**
 * Verify that account title matches CNIC name (KYC verification)
 * @param {string} cnicName - Name from CNIC/KYC records
 * @param {string} accountTitle - Account title from bank document
 * @returns {Object} - Verification result
 */
export const verifyNameMatch = (cnicName, accountTitle) => {
  if (!cnicName || !accountTitle) {
    return {
      match: false,
      confidence: 0,
      message: 'Missing name data for comparison',
      requiresManualReview: true,
    };
  }

  // Normalize names for comparison
  const normalizedCnic = cnicName.toUpperCase().trim().replace(/\s+/g, ' ');
  const normalizedAccount = accountTitle.toUpperCase().trim().replace(/\s+/g, ' ');

  // Exact match
  if (normalizedCnic === normalizedAccount) {
    return {
      match: true,
      confidence: 1.0,
      message: 'Names match exactly',
      requiresManualReview: false,
    };
  }

  // Partial match (check if all words from one appear in the other)
  const cnicWords = normalizedCnic.split(' ');
  const accountWords = normalizedAccount.split(' ');

  const matchingWords = cnicWords.filter(word => accountWords.includes(word));
  const matchRatio = matchingWords.length / Math.max(cnicWords.length, accountWords.length);

  if (matchRatio >= 0.75) {
    return {
      match: true,
      confidence: matchRatio,
      message: 'Names are similar (high confidence)',
      requiresManualReview: false,
    };
  }

  if (matchRatio >= 0.5) {
    return {
      match: false,
      confidence: matchRatio,
      message: 'Names partially match - requires manual review',
      requiresManualReview: true,
    };
  }

  return {
    match: false,
    confidence: matchRatio,
    message: 'Names do not match',
    requiresManualReview: false,
  };
};

/**
 * Verify payment receipt against expected transaction details
 * @param {Object} receiptData - OCR extracted receipt data
 * @param {Object} expectedData - Expected transaction details
 * @returns {Object} - Verification result
 */
export const verifyReceipt = (receiptData, expectedData) => {
  const issues = [];
  let matchScore = 0;
  const totalChecks = 4;

  // Check sender name/account
  if (receiptData.senderName && expectedData.accountTitle) {
    const nameMatch = verifyNameMatch(receiptData.senderName, expectedData.accountTitle);
    if (nameMatch.match) {
      matchScore++;
    } else {
      issues.push(`Sender name mismatch: ${receiptData.senderName} vs ${expectedData.accountTitle}`);
    }
  } else {
    // If sender name not in receipt, check account number
    if (receiptData.senderAccount && expectedData.accountNumber) {
      if (receiptData.senderAccount.includes(expectedData.accountNumber) ||
          expectedData.accountNumber.includes(receiptData.senderAccount)) {
        matchScore++;
      } else {
        issues.push('Sender account mismatch');
      }
    }
  }

  // Check amount
  if (receiptData.amount && expectedData.amount) {
    const receiptAmount = parseFloat(String(receiptData.amount).replace(/[^0-9.]/g, ''));
    const expectedAmount = parseFloat(String(expectedData.amount).replace(/[^0-9.]/g, ''));
    if (Math.abs(receiptAmount - expectedAmount) < 1) {
      matchScore++;
    } else {
      issues.push(`Amount mismatch: PKR ${receiptAmount} vs PKR ${expectedAmount}`);
    }
  }

  // Check reference number
  if (receiptData.referenceNumber && expectedData.referenceNumber) {
    const receiptRef = receiptData.referenceNumber.toUpperCase().replace(/[-\s]/g, '');
    const expectedRef = expectedData.referenceNumber.toUpperCase().replace(/[-\s]/g, '');
    if (receiptRef.includes(expectedRef) || expectedRef.includes(receiptRef)) {
      matchScore++;
    } else {
      issues.push(`Reference number mismatch: ${receiptData.referenceNumber} vs ${expectedData.referenceNumber}`);
    }
  }

  // Check receiver account (ANB account)
  if (receiptData.receiverAccount && expectedData.anbAccountNumber) {
    if (receiptData.receiverAccount.includes(expectedData.anbAccountNumber) ||
        expectedData.anbAccountNumber.includes(receiptData.receiverAccount)) {
      matchScore++;
    } else {
      issues.push('Receiver account does not match ANB account');
    }
  }

  const confidence = matchScore / totalChecks;

  if (confidence >= 0.75) {
    return {
      valid: true,
      confidence,
      matchScore,
      totalChecks,
      message: 'Receipt verified successfully',
      issues: [],
      requiresManualReview: false,
      action: 'approve',
    };
  }

  if (confidence >= 0.5) {
    return {
      valid: false,
      confidence,
      matchScore,
      totalChecks,
      message: 'Receipt partially verified - requires manual review',
      issues,
      requiresManualReview: true,
      action: 'manual_review',
    };
  }

  return {
    valid: false,
    confidence,
    matchScore,
    totalChecks,
    message: 'Receipt verification failed - details do not match',
    issues,
    requiresManualReview: false,
    action: 'reject_with_penalty',
  };
};

/**
 * Extract and verify CNIC document
 * @param {File} cnicImage - CNIC image file
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<Object>} - Extracted and verified data
 */
export const processCnicImage = async (cnicImage, onProgress) => {
  const ocrResult = await extractTextFromImage(cnicImage, onProgress);

  if (!ocrResult.success) {
    return { success: false, error: ocrResult.error };
  }

  const text = ocrResult.text.toUpperCase();

  // Extract CNIC number pattern (XXXXX-XXXXXXX-X)
  const cnicPattern = /(\d{5}[-\s]?\d{7}[-\s]?\d)/g;
  const cnicMatch = text.match(cnicPattern);

  // Extract name (usually after "NAME" or first line)
  const namePattern = /(?:NAME|HOLDER)[\s:]*([A-Z\s]{3,50})/gi;
  const nameMatch = text.match(namePattern);

  return {
    success: true,
    data: {
      cnicNumber: cnicMatch ? cnicMatch[0].replace(/\s/g, '') : null,
      name: nameMatch ? nameMatch[0].replace(/(?:NAME|HOLDER)[\s:]*/i, '').trim() : null,
      rawText: text,
    },
    confidence: ocrResult.confidence,
  };
};

/**
 * Extract and verify bank document (IBAN letter / cancelled cheque)
 * @param {File} bankDoc - Bank document image file
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<Object>} - Extracted and verified data
 */
export const processBankDocument = async (bankDoc, onProgress) => {
  const ocrResult = await extractTextFromImage(bankDoc, onProgress);

  if (!ocrResult.success) {
    return { success: false, error: ocrResult.error };
  }

  const text = ocrResult.text.toUpperCase();

  // Extract IBAN (Pakistan format: PK + 22 alphanumeric)
  const ibanPattern = /PK\d{2}[A-Z]{4}\d{16}/g;
  const ibanMatch = text.match(ibanPattern);

  // Extract account number
  const accountPattern = /(?:A\/C|ACCOUNT|ACC)[\s:NO.]*(\d{10,20})/gi;
  const accountMatch = text.match(accountPattern);

  // Extract account title
  const titlePattern = /(?:ACCOUNT\s*TITLE|TITLE|NAME)[\s:]*([A-Z\s]{3,50})/gi;
  const titleMatch = text.match(titlePattern);

  return {
    success: true,
    data: {
      iban: ibanMatch ? ibanMatch[0] : null,
      accountNumber: accountMatch ? accountMatch[0].match(/\d{10,20}/)?.[0] : null,
      accountTitle: titleMatch ? titleMatch[0].replace(/(?:ACCOUNT\s*TITLE|TITLE|NAME)[\s:]*/i, '').trim() : null,
      rawText: text,
    },
    confidence: ocrResult.confidence,
  };
};

/**
 * Demo verification - simulates successful OCR for testing
 * @param {Object} userData - User data for demo verification
 * @returns {Object} - Demo verification result
 */
export const demoVerification = (userData) => {
  return {
    cnicVerification: {
      success: true,
      data: {
        cnicNumber: userData.cnic,
        name: userData.name,
      },
      confidence: 98,
    },
    bankDocVerification: {
      success: true,
      data: {
        iban: userData.iban,
        accountNumber: userData.accountNumber,
        accountTitle: userData.name.toUpperCase(),
      },
      confidence: 95,
    },
    nameMatch: {
      match: true,
      confidence: 1.0,
      message: 'Names match exactly',
      requiresManualReview: false,
    },
    overallStatus: 'verified',
  };
};

export default {
  extractTextFromImage,
  parseReceiptText,
  processReceiptImage,
  verifyNameMatch,
  verifyReceipt,
  processCnicImage,
  processBankDocument,
  demoVerification,
};
